import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import Navbar from '../components/Navbar';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { Card, CardContent } from '../components/ui/card';
import { Award, Heart, Users, Stethoscope } from 'lucide-react';

const AmbassadorsPage = () => {
  const [ambassadors, setAmbassadors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAmbassadors();
  }, []);

  const loadAmbassadors = async () => {
    try {
      const response = await api.getAmbassadors();
      setAmbassadors(response.data.ambassadors || []);
    } catch (err) {
      console.error('Failed to load ambassadors:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <main className="pt-20 pb-16 main-content">
        <div className="max-w-4xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mx-auto mb-6">
              <Award className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-4xl font-semibold text-slate-900 tracking-tight mb-4">
              NoSpin-EBM Ambassadors
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              These generous supporters help keep NoSpin-EBM free and independent. 
              Their contributions fund development, server costs, and ensure evidence-based 
              medicine remains accessible to all clinicians.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-12 max-w-md mx-auto">
            <Card className="border-slate-200">
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900">{ambassadors.length}</p>
                <p className="text-sm text-slate-500">Ambassadors</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 text-center">
                <Heart className="h-6 w-6 text-red-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900">∞</p>
                <p className="text-sm text-slate-500">Searches Enabled</p>
              </CardContent>
            </Card>
          </div>

          {/* Ambassadors Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
            </div>
          ) : ambassadors.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  Be the First Ambassador
                </h3>
                <p className="text-slate-500 max-w-md mx-auto">
                  Donate $100 or more to become a NoSpin-EBM Ambassador. 
                  Your name will appear here, and you'll enjoy lifetime access 
                  without donation prompts.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ambassadors.map((ambassador, index) => (
                <Card 
                  key={index} 
                  className="border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`ambassador-card-${index}`}
                >
                  <div className="h-2 bg-gradient-to-r from-amber-400 to-orange-400" />
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center shrink-0">
                        <span className="text-lg font-bold text-amber-700">
                          {(ambassador.name || 'A').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {ambassador.name || 'Anonymous'}
                        </h3>
                        {ambassador.specialty && (
                          <p className="text-sm text-slate-500 flex items-center gap-1 truncate">
                            <Stethoscope className="h-3.5 w-3.5 shrink-0" />
                            {ambassador.specialty}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 text-center">
            <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 inline-block">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-2">
                  Become an Ambassador
                </h3>
                <p className="text-sm text-slate-600 mb-4 max-w-sm">
                  A one-time donation of $100+ grants lifetime ambassador status 
                  and helps keep NoSpin-EBM free for everyone.
                </p>
                <div className="flex items-center justify-center gap-2 text-amber-700">
                  <Award className="h-5 w-5" />
                  <span className="font-medium">Lifetime recognition</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <DisclaimerBanner />
    </div>
  );
};

export default AmbassadorsPage;
